import { View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { Employee, Visa } from "../../../features/process/types/processTypes";
import JsBarcode from "jsbarcode";
import { AuthUser } from "../../../features/auth/types/authType";

interface Props {
  employee: Employee;
  user: AuthUser;
}
const visaCanvas = document.createElement("canvas");
const enjazCanvas = document.createElement("canvas");

const BARCODE_RENDER_SCALE = 7;
const BARCODE_BAR_HEIGHT = 40;
const BARCODE_MARGIN = 0;

const barcodeMargin = BARCODE_MARGIN * BARCODE_RENDER_SCALE;

const barcodeOptions = {
  format: "code128a" as const,
  width: BARCODE_RENDER_SCALE,
  height: BARCODE_BAR_HEIGHT * BARCODE_RENDER_SCALE,
  displayValue: false,
  marginTop: barcodeMargin,
  marginBottom: barcodeMargin,
  marginRight: barcodeMargin,
  marginLeft: barcodeMargin,
  background: "#ffffff",
  lineColor: "#000000",
};

const leftBarcodeOptions = {
  ...barcodeOptions,
  marginLeft: 0,
};

const barcodeImageStyle = (canvas: HTMLCanvasElement) => ({
  width: canvas.width / BARCODE_RENDER_SCALE,
  height: canvas.height / BARCODE_RENDER_SCALE,
});

// JsBarcode throws on values that are invalid for the chosen format (e.g. a
// lowercase letter in a code128a value). Render safely so a bad visa/enjaz
// number can't crash the page that renders this report.
const renderBarcode = (
  canvas: HTMLCanvasElement,
  value: string | undefined,
  options: typeof barcodeOptions,
) => {
  try {
    JsBarcode(canvas, value as string, options);
  } catch {
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
  }
};

const styles = StyleSheet.create({
  headerContainer: {
    marginTop: 15,
    paddingLeft: 0,
    paddingRight: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  leftColumnContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    fontFamily: "Open Sans",
    marginLeft: 0,
  },
  centerColumnContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 50,
  },
  rightColumnContainer: {
    width: "33%",
    flexDirection: "column",
    alignItems: "center",
  },

  sponsorContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginTop: -2,
    marginLeft: 4,
  },
  label: {
    width: 45,
  },
  text: {
    textAlign: "left",
    fontFamily: "Open Sans",
    fontWeight: "bold",
  },
  photo: {
    width: 95,
    height: 100,
    marginLeft: 4,
  },
  mofaLogo: {
    width: 70,
    height: 80,
    marginLeft: "auto",
    marginRight: "auto",
  },
  consularImage: {
    width: 150,
    height: 60,
  },
});
export const BarcodeHeader = ({ employee, user }: Props) => {
  renderBarcode(visaCanvas, employee.visa?.visaNumber, leftBarcodeOptions);
  renderBarcode(enjazCanvas, employee.embassyProcess?.enjazNumber, barcodeOptions);
  const visaNoBarcode = visaCanvas.toDataURL();
  const enjazNoBarcode = enjazCanvas.toDataURL();
  const face = employee.facePhoto as string;
  const visa = employee.visa as Visa;
  //const embassy = employee.embassy;
  // const [faceImage, setFaceImage] = useState("./cvPlusLogo2.png");
  // useEffect(() => {
  //   if (face.length > 1) {
  //     console.log(face);
  //     fetch(face, { mode: "no-cors" })
  //       .then(function (response) {
  //         return response.blob();
  //       })
  //       .then(function (blob) {
  //         // here the image is a blob
  //         setFaceImage(URL.createObjectURL(blob));
  //       });
  //   }
  // }, [employee]);
  return (
    <View style={styles.headerContainer}>
      <View style={styles.leftColumnContainer}>
        <Image style={barcodeImageStyle(visaCanvas)} source={visaNoBarcode} />
        <View style={styles.sponsorContainer}>
          <Text style={[styles.label, { fontSize: 10 }]}>Visa No.: </Text>
          <Text style={[styles.text, { fontSize: 10 }]}>{visa.visaNumber}</Text>
        </View>
        <View style={styles.sponsorContainer}>
          <Text style={[styles.label, { fontSize: 10 }]}>Sponsor: </Text>
          <Text style={[styles.text, { fontSize: 10, fontFamily: "Noto" }]}>
            {visa?.fullName}
          </Text>
        </View>
        <Image style={styles.photo} source={face} />
      </View>
      <View style={styles.centerColumnContainer}>
        <Image style={styles.mofaLogo} source="/mofaLogo.jpg" />
      </View>
      <View style={styles.rightColumnContainer}>
        <Image style={barcodeImageStyle(enjazCanvas)} source={enjazNoBarcode} />
        <Text style={[styles.text, { fontSize: 10, marginTop: -2 }]}>
          {employee.embassyProcess?.enjazNumber}
        </Text>
        <Text
          style={{
            fontFamily: "Noto",
            fontSize: 8,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: 5,
          }}
          wrap
        >
          سفارة المملكة العربية السعودية
        </Text>
        <Text
          style={{
            fontFamily: "Noto",
            fontSize: 8,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: -1,
          }}
          wrap
        >
          القسم القنصلي
        </Text>
        <Text
          style={{
            fontFamily: "Open Sans",
            fontSize: 8,
            fontWeight: "bold",
            textAlign: "center",
          }}
          wrap
        >
          EMBASSY OF SAUDI ARABIA
        </Text>
        <Text
          style={{
            fontFamily: "Open Sans",
            fontSize: 8,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: -1,
          }}
          wrap
        >
          CONSULAR SECTION
        </Text>
        <Text
          style={{
            fontFamily: "Open Sans",
            fontSize: 10,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: 8,
          }}
          wrap
        >
          {employee?.owner?.displayName.toUpperCase()}
        </Text>
        <Text
          style={{
            fontFamily: "Open Sans",
            fontSize: 10,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: 2,
          }}
          wrap
        >
          {employee?.owner?.email.toLowerCase()}
        </Text>
      </View>
    </View>
  );
};
